export interface TestStepCodeFn {
  fn_name?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args: (any | Record<string, any> | null)[];
}

export interface TestStepCode extends TestStepCodeFn {
  tag_name?: string;
  id?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: Record<string, any>;
  text_content?: string;
  role?: string;
  accessible_name?: string;
  fn_pipe: TestStepCodeFn[];
}

// Enums
export enum DBStatus {
  ACTIVE = "active",
  DELETED = "deleted",
}

export enum ViewportType {
  BIG_DESKTOP = "big_desktop",
  DESKTOP = "desktop",
  TABLET = "tablet",
  MOBILE = "mobile",
  CUSTOM = "custom",
}

export enum AppContextType {
  TEXT = "text",
  DOCUMENT = "document",
  JSON = "json",
}

export enum RecordingProcessingStatus {
  RECORDING = "recording",
  PENDING = "pending",
  PROCESSING = "processing",
  PROCESSED = "processed",
  ERROR = "error",
}

export enum TestRunStatus {
  PENDING = "pending",
  RUNNING = "running",
  PASSED = "passed",
  FAILED = "failed",
  FAILED_PENDING = "failed_pending",
  SKIPPED = "skipped",
  ERROR = "error",
  CANCELLED = "cancelled",
  TIMED_OUT = "timed_out",
  FLAKY = "flaky",
  NO_CREDITS = "no_credits",
}

export const TestRunStatusList: TestRunStatus[] = [
  TestRunStatus.PENDING,
  TestRunStatus.RUNNING,
  TestRunStatus.PASSED,
  TestRunStatus.FAILED,
  TestRunStatus.FAILED_PENDING,
  TestRunStatus.SKIPPED,
  TestRunStatus.ERROR,
  TestRunStatus.CANCELLED,
  TestRunStatus.TIMED_OUT,
  TestRunStatus.FLAKY,
  TestRunStatus.NO_CREDITS,
];

export type UserFlowSeverity = "low" | "medium" | "high" | "critical";
export type UserFlowPriority = "low" | "medium" | "high" | "urgent";
export type UserFlowConfidence = "low" | "medium" | "high" | "very_high";

// Shared base types
export interface AuditBase {
  organization_id?: string;
  created_at: string; // datetime
  created_by: string;
  updated_at?: string;
  updated_by?: string;
  deleted_at?: string;
  deleted_by?: string;
  status: DBStatus;
}

export interface ViewportConfig {
  base_url?: string;
  width?: number;
  height?: number;
  vp_type: ViewportType;

  // Advanced settings
  user_agent?: string;
  locale?: string;
  timezone?: string;
  geolocation?: {
    latitude: number;
    longitude: number;
  };
}

// Types
export type AppConfigType = "production" | "staging" | "development" | "local";

// Models
export interface AppConfig extends AuditBase, ViewportConfig {
  id: string;
  name: string;
  cfg_type?: AppConfigType;
  login_url?: string;
  login_username?: string;
  login_password?: string;
  login_mfa_secret?: string;
  login_instructions?: string;
  login_steps?: TestStepCode[];
  failure_status?: TestRunStatus.FAILED | TestRunStatus.FAILED_PENDING;
  recordings?: Recording[];
  tests?: Test[];
  contexts?: AppContext[];
  api_keys?: APIKey[];
  remove_popups?: boolean;
}

export interface AppContext extends AuditBase {
  id: string;
  context_type: AppContextType;
  file_path?: string;
  file_name?: string;
  context_raw_text?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context_info: Record<string, any>;
  app_config_id?: string;
  app_config?: AppConfig;
}

export interface Persona extends AuditBase {
  id: string;
  app_config_id?: string;
  app_config?: AppConfig;
  name: string;
  description?: string;
  system_prompt: string;
  user?: string;
  password?: string;
}

export interface PersonaCreateSchema {
  app_config_id?: string;
  name: string;
  description?: string;
  system_prompt: string;
  user?: string;
  password?: string;
}

export interface PersonaUpdateSchema {
  app_config_id?: string;
  name?: string;
  description?: string;
  system_prompt?: string;
  user?: string;
  password?: string;
}

export interface PersonaResponseSchema extends AuditBase {
  id: string;
  app_config_id?: string;
  name: string;
  description?: string;
  system_prompt: string;
  user?: string;
  password?: string;
}

export interface APIKey extends AuditBase {
  id: string;
  name: string;
  key: string;
  scope?: "admin" | "api" | "mcp" | "recordings" | "webhooks" | "pw_reporter";
  expiration_date?: string;
  app_config_id?: string;
  app_config?: AppConfig;
  last_used_at?: string;
  last_used_by?: string;
  last_used_ip?: string;
  ws_url?: string;
}

export interface RecordingParsedEvent extends AuditBase {
  id: string;
  recording_id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: any; // EventWithTime (assumed to be a generic JSON structure)
}

export interface Recording extends AuditBase, ViewportConfig {
  id: string;
  name: string;
  events_url?: string;
  processing_status: RecordingProcessingStatus;
  app_config_id: string;
  app_config?: AppConfig;
  events_path?: string;
  parsed_events_path?: string;
  tests?: Test[];
  test_steps?: TestStep[];
}

export type TestSuiteFailureStrategy = "stop_on_failure" | "continue_on_failure";
export const TEST_SUITE_FAILURE_STRATEGY_LIST: TestSuiteFailureStrategy[] = [
  "stop_on_failure",
  "continue_on_failure",
];

export interface TestSuite extends AuditBase {
  id: string;

  name: string;
  description?: string;

  // CRON syntax
  schedule?: string;
  schedule_enabled: boolean;

  webhook_ids: string[];
  test_ids: string[];

  // Tests explicitly added to the suite (not via tag filters)
  // test_ids is computed as: explicit_test_ids ∪ tag-matched tests
  explicit_test_ids?: string[];

  hook_ids: string[];

  slack_channel_override?: string;

  failure_strategy?: TestSuiteFailureStrategy | null;
  max_concurrent_runs?: number | null;

  // Tag-based test filtering (dynamic test inclusion)
  tag_filters?: string[];

  test_suite_runs?: TestSuiteRun[];

  test_dependency_graph?: {
    [test_id: string | "root"]: string[];
  };

  test_dependency_graph_conditions?: {
    [test_id: string]: string[];
  };

  // PFS (Probabilistic Flakiness Score) fields
  pfs_score?: number;
  pfs_bad_state_prob?: number;
  pfs_confidence_lower?: number;
  pfs_confidence_upper?: number;
  pfs_num_observations?: number;

  // Average PFS of all tests in the suite
  tests_pfs_score?: number;
}

export interface TestSuiteDetails extends TestSuite {
  tests: Test[];
}

export type TestSuiteSchema = {
  name: string;
  description?: string;
  schedule?: string;
  schedule_enabled?: boolean;
  test_ids: string[];
  webhook_ids?: string[];
  slack_channel_override?: string;
  failure_strategy?: TestSuiteFailureStrategy | null;
  tag_filters?: string[];
  test_dependency_graph?: {
    [test_id: string | "root"]: string[];
  };
  max_concurrent_runs?: number | null;
  test_dependency_graph_conditions?: {
    [test_id: string]: string[];
  };
};

export type TestVariableLifetime = "all" | "test" | "suite";

export const TEST_VARIABLE_LIFETIME_LIST: TestVariableLifetime[] = ["all", "test", "suite"];

export type TestVariableContext = "test" | "suite" | "persona" | "app_config" | "global";

export const TEST_VARIABLE_CONTEXT_LIST: TestVariableContext[] = [
  "test",
  "suite",
  "persona",
  "app_config",
  "global",
];

export type TestVariableType =
  | "custom"
  | "company_name"
  | "random_string"
  | "random_number"
  | "first_name"
  | "last_name"
  | "full_name"
  | "username"
  | "address"
  | "phone_number"
  | "city"
  | "country"
  | "url"
  | "email"
  | "password"
  | "email_message"
  | "sensitive"
  | "text"
  | "uuid"
  | "slug";

export const TEST_VARIABLE_TYPE_LIST: TestVariableType[] = [
  "custom",
  "company_name",
  "random_string",
  "random_number",
  "first_name",
  "last_name",
  "full_name",
  "username",
  "address",
  "phone_number",
  "city",
  "country",
  "url",
  "email",
  "password",
  "email_message",
  "sensitive",
  "text",
  "uuid",
  "slug",
];

export interface TestVariable {
  key: string;
  value: string | number;
  type: TestVariableType;
  lifetime: TestVariableLifetime;
  lifetime_index?: number | null;
  context: TestVariableContext;
  is_sensitive: boolean;
}

export type TestSource = "manual" | "recording" | "generate" | "scenario" | "discovery";

export const TestSourceList: TestSource[] = [
  "manual",
  "recording",
  "generate",
  "scenario",
  "discovery",
];

export type TestVariantType = "discovery_edge_case" | "auto_edge_case";

export interface TestMatrixOption {
  id: string;
  is_default: boolean;
  var_options: TestVariable[]; // You'll need to define TestVariable separately
  timeout_override?: number | null;
}

export interface TestMatrix {
  max_parallel?: number | null;
  options: TestMatrixOption[];
}

export interface Test extends AuditBase {
  id: string;
  name: string;
  variant?: "recording" | "agent" | "manual" | "pw_reporter";
  template_code?: string;
  app_config_id: string;
  app_config?: AppConfig;
  test_variant_type?: TestVariantType;
  test_variant_for?: string;
  matrix?: TestMatrix;
  recording_id: string;
  recording?: Recording;
  run_status?: TestRunStatus;
  steps?: TestStep[];
  graph_path?: GraphEdge[];
  variables?: TestVariable[];
  test_runs?: TestRun[];
  test_dependency_ids?: string[];
  source?: TestSource;
  source_id?: string;
  version_hash?: string;
  is_positive?: boolean;
  success_criteria?: string;
  reasoning?: string;
  confidence?: UserFlowConfidence;
  severity?: UserFlowSeverity;
  priority?: UserFlowPriority;
  available_data_asset_ids?: string[];

  pfs_score?: number;
  pfs_bad_state_prob?: number;
  pfs_num_observations?: number;

  // Lineage tracking
  lineage_id?: string;

  // Tags for filtering and categorization
  tags?: string[];

  // AAA description for test intent
  aaa_description?: string;
}

export interface PaginatedTestsResponse {
  items: Test[];
  total: number;
}

export interface TestStep extends AuditBase {
  id: string;
  name: string;
  description?: string;
  code?: string;
  code_info: TestStepCode[];
  test_id: string;
  test?: Test;
  recording_id: string;
  recording?: Recording;
}

export interface TestRunStep extends AuditBase {
  id: string;
  block_id?: number;
  type?:
    | "dependency_run"
    | "cached_dependency"
    | "block_run"
    | "pw_fixture"
    | "pw_hook"
    | "pw_run"
    | "check_run";
  // For check_run steps, the linked CheckRun id (DES-468).
  entity_id?: string;
  name: string;
  description?: string;
  code?: string;
  elapsed_seconds?: number;
  logs?: string[];
  error_message?: string;
  url?: string;
  run_status: TestRunStatus;
  test_run_id: string;
  test_run?: TestRun;
  test_step_id: string;
  test_step?: TestStep;
  title_path?: string[]; // Hierarchical path for tree rendering
  // Retry information (for real-time UI updates)
  retry_attempt?: number;
  max_retries?: number;
  retry_wait_seconds?: number;
  retry_phase?: "initial" | "fixing";
  context_name?: string;
}

export type ClipboardOperation = "read" | "write";

export interface ClipboardItem {
  page_url: string;
  timestamp: string; // ISO 8601 datetime (e.g., "2025-10-05T12:34:56Z")
  operation: ClipboardOperation;
  content: string;
}

export interface TestRun extends AuditBase {
  id: string;
  rerun_id?: string;
  matrix_option_id?: string;
  external_id?: string;
  name: string;
  test_id: string;
  test?: Test;
  run_status: TestRunStatus;
  final_run_status?: TestRunStatus;
  final_comment_id?: string;
  result?: string;
  error_message?: string;
  recording_path?: string;
  har_path?: string;
  context_recordings?: Record<string, string>;
  context_hars?: Record<string, string>;
  context_live_views?: Record<string, string>;
  storage_state_path?: string;
  live_view_url?: string;
  start_time?: string;
  end_time?: string;
  steps?: TestRunStep[];
  used_variables?: TestVariable[];
  allow_fix?: boolean;
  clipboard?: ClipboardItem[];
  test_suite_run_id?: string;
  // Git info from Playwright Reporter
  git_branch?: string;
  git_commit_sha?: string;
  git_commit_sha_short?: string;
  git_commit_message?: string;
  git_author_name?: string;
  git_author_email?: string;
  git_commit_timestamp?: string;
  git_remote_origin?: string;
  git_is_dirty?: boolean;
}

export interface TestRunLogs extends AuditBase {
  id: string;
  test_id: string;
  test_run_id: string;
  text: string;
  type?: string; // e.g. 'info', 'error', 'debug'
}

export type TestSuiteRunTrigger = "manual" | "cron" | "webhook" | "action" | "pw_reporter";

export interface TestSuiteRun extends AuditBase {
  id: string;
  external_id?: string;

  run_status: TestRunStatus;
  final_run_status?: TestRunStatus;
  final_comment_id?: string;

  result?: string;
  error_message?: string;

  start_time?: string;
  end_time?: string;
  duration_seconds?: number;

  trigger: TestSuiteRunTrigger;

  test_runs?: TestRun[];
  test_suite_id: string;
  test_suite?: TestSuite;
  test_suite_name?: string; // Added for the suite name
  // Git info from Playwright Reporter
  git_branch?: string;
  git_commit_sha?: string;
  git_commit_sha_short?: string;
  git_commit_message?: string;
  git_author_name?: string;
  git_author_email?: string;
  git_commit_timestamp?: string;
  git_remote_origin?: string;
  git_is_dirty?: boolean;

  // CI environment info from Playwright Reporter
  ci_run_id?: string;
  ci_metadata?: {
    provider?: string;
    run_id?: string;
    run_attempt?: string;
    workflow?: string;
    job?: string;
    actor?: string;
    repository?: string;
    ref?: string;
    run_url?: string;
    [key: string]: unknown;
  };

  // Shard info
  shard_total?: number;
  shards_completed?: number;
  shard_run_ids?: string[];
}

export interface ApiConfig {
  configs: AppConfig[];
  api_keys: APIKey[];
}

export interface LocatorStep {
  method?: string;
  args?: unknown[];
  kwargs?: Record<string, unknown>;
  attribute?: string;
}

export type SelectorStrategy =
  | "aria"
  | "test_id"
  | "semantic"
  | "stable_class"
  | "text"
  | "css"
  | "xpath"
  | "placeholder"
  | "label"
  | "id"
  | "parent_based"
  | "nth_match"
  | "playwright_internal"
  | "fallback"
  | "ai_based";

export type ValueStrategy = "static" | "ai_based";

export interface LocatorInstruction {
  chain: LocatorStep[];
  text?: string;
  strategy_used?: SelectorStrategy | string;
}

export const ACTION_INSTRUCTION_ACTIONS = [
  // Core interactions
  "click",
  "click_download",
  "dblclick",
  "tap",
  "hover",
  "press",
  "fill",
  "type",
  "check",
  "uncheck",
  "set_checked",
  "select_option",
  "select_text",
  "scroll",
  "scroll_into_view_if_needed",
  "drag_and_drop",
  "relative_drag_and_drop",
  "blur",
  "highlight",
  "wheel",
  // Navigation and page control
  "goto",
  "wait_for_selector",
  "wait_for_timeout",
  "wait_for_load_state",
  // JS evaluation
  "evaluate",
  "evaluate_handle",
  // File management
  "set_input_files",
  "handle_file_chooser",
  // AI
  "ai_action",
  "ai_assertion",
  "ai_auto_close_popup",
  "extract_from_file",
  // Email management
  "get_last_email",
  "reply_to_email",
  // Variables
  "new_task_variable",
  "get_task_variable",
  "get_task_variables",
  "set_task_variable",
  "variable_json_path",
  // Data
  "extract_structured_data",
  "extract_from_page",
  "ai_validate_variable",
  // Browser requests
  "parse_request",
  "perform_request",
  // API checks
  "api_check",
  // Assertions — Visibility
  "wait",
  "to_be_visible",
  "not_to_be_visible",
  "to_be_hidden",
  "not_to_be_hidden",
  // Assertions — State
  "to_be_enabled",
  "not_to_be_enabled",
  "to_be_disabled",
  "not_to_be_disabled",
  "to_be_editable",
  "not_to_be_editable",
  "to_be_focused",
  "to_be_checked",
  "not_to_be_checked",
  "to_be_unchecked",
  "to_be_empty",
  "not_to_be_empty",
  // Assertions — URLs
  "to_have_url",
  // Assertions — Content
  "to_contain_text",
  "not_to_contain_text",
  "text_content", // Deprecated, use `to_have_text` instead
  "to_have_text",
  "not_to_have_text",
  "to_have_value",
  "not_to_have_value",
  "to_have_values",
  "not_to_have_values",
  // Special action
  "go_back",
  "go_forward",
  "reload",
] as const;

export type ActionType = (typeof ACTION_INSTRUCTION_ACTIONS)[number];

export interface AnyDict {
  text?: string;

  has_text?: string;
  _regex?: string;

  files?: string[];

  name?: string;
  exact?: boolean;

  button?: string;

  disabled?: boolean;

  x?: number;
  y?: number;

  delta_x?: number;
  delta_y?: number;

  force?: boolean;
  wait_new_page?: boolean;

  key?: string;
  delay?: number;

  value?: string;
  index?: number;
  label?: string;

  url?: string;

  file_paths?: string[];
  file_name?: string;

  email?: string;
  subject?: string;
  reply_body?: string;

  email_id?: string;

  timeout?: number;
  wait_until?: "load" | "domcontentloaded" | "networkidle";

  variable_name?: string | string[];
  variable_type?: string;
  length?: number;
  custom_value?: number | string;

  variable_validation?: string;

  extraction_task?: string;
  extraction_variable_target_name?: string;

  json_path?: string;

  request_method?: string;
  request_url?: string;

  request_headers?: Record<string, string>;
  request_body?: string; // JSON string

  request_expected_status?: number;

  check_id?: string;
}

export interface ActionInstruction {
  action: ActionType;
  value?: string | number | AnyDict;
  timeout?: number;
  max_mode?: boolean;
  ultra_mode?: boolean;
  // AI-based value resolution
  value_strategy?: ValueStrategy | string;
  value_semantic_text?: string;
  value_variable_name?: string;
}

export type ConfidenceLevel = "low" | "medium" | "high";

export interface Block {
  id: number;

  name: string;
  description?: string;
  end_goal?: string;

  group?: string;
  is_group_check?: boolean;

  should_skip?: boolean;
  should_fail?: boolean;

  set_variable_name?: string;
  use_variable_name?: string;

  is_async: boolean;

  code: string; // Playwright code representing the block

  locator: LocatorInstruction;
  action?: ActionInstruction;

  fixed: boolean;
  skipped: boolean;

  is_flaky: boolean;

  should_fix_with_ai: boolean;
  should_try_deterministic: boolean;

  logs?: string[];
  last_error?: string;

  reasoning?: string;

  confidence?: ConfidenceLevel;
  confidence_reasoning?: string;

  original_task?: string;

  aaa_phase?: "arrange" | "act" | "assert";

  compute_semantic_diff?: boolean;

  pre_screenshot_path?: string;
  post_screenshot_path?: string;
}

export interface ParsedCode {
  blocks: Block[];
}

export interface StepAssertion {
  state_invariant: string;
  qa_checks: string[];
}

export interface BaseStepAction {
  order: number;
  name: string;
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any[];
}

export interface TestContextDetails {
  screenshot?: string;
  name?: string;
  description?: string;
}

export interface TestContext extends AuditBase {
  id: string;

  test_id: string;
  test?: Test;

  start_timestamp: number;
  started_at: string;

  end_timestamp: number;
  ended_at: string;

  disabled?: boolean;

  events_path?: string;

  name: string;
  description?: string;
  end_goal?: string;

  pre_conditions?: StepAssertion[];
  post_conditions?: StepAssertion[];

  actions?: BaseStepAction[];

  diff_screenshot?: string;
  start_details?: TestContextDetails;
  end_details?: TestContextDetails;

  data?: Block;
}

export type ExternalProviderConfigSlack = {
  token?: string; // Optional when using OAuth integration
  channel: string;
};

export type ExternalProviderConfigEmail = {
  email: string;
};

export type ExternalProviderConfigDataSlack = {
  enabled: boolean;
  provider: "slack";
  config: ExternalProviderConfigSlack;
  oauth_integration_id?: string; // Link to OAuth integration for secure token storage
};

export type ExternalProviderConfigDataEmail = {
  enabled: boolean;
  provider: "email";
  config: ExternalProviderConfigEmail;
  oauth_integration_id?: string; // For future OAuth email providers
};

export type ExternalProviderConfigData =
  | ExternalProviderConfigDataSlack
  | ExternalProviderConfigDataEmail;

export type ExternalProviderConfig = AuditBase &
  ExternalProviderConfigData & {
    id: string;
  };

export function isExternalProviderConfigSlack(
  config: ExternalProviderConfigData,
): config is ExternalProviderConfigDataSlack {
  return config.provider === "slack";
}

export function isExternalProviderConfigEmail(
  config: ExternalProviderConfigData,
): config is ExternalProviderConfigDataEmail {
  return config.provider === "email";
}

export type TestAgentResponseBaseMessage = {
  ts: string;
  text: string;
  variant: "loading" | "event";
};

export type GraphEdgeType = "opens" | "contains";
export type GraphNodeType = "page" | "interaction" | "content" | "dependency";
export type ContentType = "text" | "list" | "image" | "video" | "audio";
export type InteractionType = "button" | "link" | "input" | "checkbox" | "radio" | "dropdown";

export interface GraphEdge {
  source: string;
  target: string;
  type: GraphEdgeType;
}

export interface GraphNode {
  id: string;
  type: GraphNodeType;
  name: string;
  description?: string;

  content_type?: ContentType;
  content_meaning?: string;

  url?: string;

  interaction_selector?: string;
  interaction_type?: InteractionType;
}

export interface Graph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface GraphInfo extends AuditBase {
  id: string;
  app_config_id: string;
  graph: Graph;
}

export interface AgentActionResult {
  is_done: boolean;
  success: boolean | null;
  extracted_content: string;
  error: string | null;
  include_in_memory: boolean;
}

export interface AgentDoneAction {
  text: string;
  success: boolean;
}

export interface AgentInteractedElement {
  tag_name: string;
  xpath: string;
  highlight_index: number;
  entire_parent_branch_path: string[];
  attributes: Record<string, string>;
  shadow_root: boolean;
  css_selector: string;
  page_coordinates: unknown | null;
  viewport_coordinates: unknown | null;
  viewport_info: unknown | null;
}

export interface AgentClickElementByIndexAction {
  index: number;
}

export interface AgentAction {
  done?: AgentDoneAction;
  click_element_by_index?: AgentClickElementByIndexAction;
  interacted_element: AgentInteractedElement | null;
  record_previous_step: unknown | null;
}

export interface AgentActionStep {
  index: number;
  action_name: string;
  action_result: AgentActionResult;
  action: AgentAction;
}

export type TestAgentConfigTask = {
  ts: string;
  task: string;
  state: "pending" | "running" | "failed";
  started_at?: string | null;
  ended_at?: string | null;
  elapsed?: number | null;
};

export type AgentRunStatus = "success" | "failure" | "unclear" | "pending_human";

export interface AgentRunResult {
  issues: string[];
  human_question?: string;
  status: AgentRunStatus;
  explanation: string;
}

export type TestAgentConfig = {
  state:
    | "waiting"
    | "idle"
    | "loading"
    | "saving"
    | "running"
    | "paused"
    | "testing"
    | "stopping"
    | "stopped"
    | "processing";
  app_config_id?: string;
  prompt: string;
  prompt_context?: string | null;
  last_result?: string | null;
  last_result_parsed?: AgentRunResult | null;
  last_result_success?: boolean | null;
  tasks: TestAgentConfigTask[];
  test_deps_ran: boolean;
  test: Test;
  test_ctxs: TestContext[];
  liveview_id: string;
  liveview_url: string;
  test_liveview_id: string;
  test_liveview_url: string;
  test_recording_path?: string | null;
  sensitive_data?: {
    [key: string]: string;
  };
  variables: TestVariable[];
  history: AgentActionStep[];
  code?: string | null;
  parsed_code?: ParsedCode | null;
  test_dep_ids: string[];
  base_url?: string | null;
  browser_url?: string | null;
  test_browser_url?: string | null;
  graph: Graph;
  graph_path?: GraphEdge[] | null;
  started_at: string | null;
  last_checkpoint: string | null;
  base_llm: string | null;
  planner_llm: string | null;
  available_actions: AvailableAction[];
};

export type TestAgentResponseMessageConfig = TestAgentResponseBaseMessage & {
  type: "config";
  data: TestAgentConfig;
};

export type TestAgentResponseMessageTask = TestAgentResponseBaseMessage & {
  type: "new_task";
  data: {
    task: string;
  };
};

export type TestAgentResponseMessageTestBlockRun = TestAgentResponseBaseMessage & {
  type: "test_block_run";
  data: {
    elapsed: number;
    status: TestRunStatus;
    block: Block;
  };
};

export type TestAgentResponseMessageTestRun = TestAgentResponseBaseMessage & {
  type: "test_run";
  data: {
    elapsed: number;
    status: TestRunStatus;
  };
};

export type TestAgentResponseMessageStep = TestAgentResponseBaseMessage & {
  type: "step";
  data: {
    step?: {
      index: number;
      name: string;
      description?: string;
      step_type: "assertion" | "action";
      pre_conditions: StepAssertion[];
      post_conditions: StepAssertion[];
      status: "success" | "failure";
      failure_reason?: string | null;
      auto_healed?: boolean;
      auto_healed_reason?: string | null;
    } | null;
  };
};

export type TestAgentResponseMessage =
  | TestAgentResponseMessageConfig
  | TestAgentResponseMessageTask
  | TestAgentResponseMessageTestRun
  | TestAgentResponseMessageTestBlockRun
  | TestAgentResponseMessageStep;

export interface DailyTestStatus {
  passed: number;
  failed: number;
}

export interface DashboardMetrics {
  total_tests: number;
  total_test_runs: number;
  total_recordings: number;
  success_rate: number; // constrained between 0.0 and 100.0 in Python
  failure_rate: number; // constrained between 0.0 and 100.0 in Python
  test_status_by_date: Record<string, DailyTestStatus>; // 'date' becomes ISO string
  recent_test_durations: Record<string, Record<string, number>>;
}

// ===== Analytics Types =====

export interface TestRunSummary {
  test_id: string;
  test_name: string;
  total_runs: number;
  passed: number;
  failed: number;
  pass_rate: number;
  avg_duration_seconds: number;
  last_run_at?: string;
}

export interface TestDurationInfo {
  test_id: string;
  test_name: string;
  avg_duration_seconds: number;
  max_duration_seconds: number;
  run_count: number;
}

export interface SuiteRunSummary {
  suite_id: string;
  suite_name: string;
  total_runs: number;
  passed: number;
  failed: number;
  pass_rate: number;
  avg_duration_seconds: number;
  last_run_at?: string;
}

export interface SuiteDurationInfo {
  suite_id: string;
  suite_name: string;
  avg_duration_seconds: number;
  max_duration_seconds: number;
  run_count: number;
}

export interface FlakyTestInfo {
  test_id: string;
  test_name: string;
  pfs_score: number;
  pfs_confidence_lower?: number;
  pfs_confidence_upper?: number;
  pfs_num_observations?: number;
  last_flaky_run_at?: string;
}

export interface FlakySuiteInfo {
  suite_id: string;
  suite_name: string;
  pfs_score: number;
  tests_pfs_score?: number;
  pfs_confidence_lower?: number;
  pfs_confidence_upper?: number;
  pfs_num_observations?: number;
  last_flaky_run_at?: string;
}

export interface TestCoverageInfo {
  test_id: string;
  test_name: string;
  last_run_at?: string;
  days_since_last_run?: number;
  created_at: string;
}

export interface OverviewMetrics {
  total_tests: number;
  total_test_runs: number;
  total_suite_runs: number;
  overall_pass_rate: number;
  overall_failure_rate: number;
  avg_test_duration_seconds: number;
  test_runs_by_day: Record<string, DailyTestStatus>;
  suite_runs_by_day: Record<string, DailyTestStatus>;
}

export interface TestRunMetrics {
  runs_by_status: Record<string, number>;
  runs_by_test: TestRunSummary[];
  longest_tests: TestDurationInfo[];
}

export interface SuiteRunMetrics {
  runs_by_status: Record<string, number>;
  runs_by_trigger: Record<string, number>;
  runs_by_suite: SuiteRunSummary[];
  longest_suites: SuiteDurationInfo[];
}

export interface FlakinessMetrics {
  // Test metrics
  avg_pfs_score: number;
  flaky_tests_count: number;
  top_flaky_tests: FlakyTestInfo[];
  pfs_distribution: Record<string, number>;

  // Suite metrics
  avg_suite_pfs_score: number;
  avg_suite_tests_pfs_score: number;
  flaky_suites_count: number;
  top_flaky_suites: FlakySuiteInfo[];
  suite_pfs_distribution: Record<string, number>;
}

export interface CoverageMetrics {
  tests_never_run: TestCoverageInfo[];
  tests_not_run_recently: TestCoverageInfo[];
  total_tests: number;
  tests_with_runs: number;
  coverage_percentage: number;
}

export interface AnalyticsResponse {
  overview: OverviewMetrics;
  test_runs: TestRunMetrics;
  suite_runs: SuiteRunMetrics;
  flakiness: FlakinessMetrics;
  coverage: CoverageMetrics;
}

export type AnalyticsPeriod = "7d" | "30d" | "90d" | "all";

// ===== Time Series Analytics Types =====

export type TimeGranularity = "day" | "week" | "month";

export interface TimeSeriesDataPoint {
  period_start: string;
  value: number;
  group_id?: string;
  group_name?: string;
}

export interface TimeSeriesGroup {
  id: string;
  name: string;
}

export interface TimeSeriesResponse {
  data: TimeSeriesDataPoint[];
  granularity: TimeGranularity;
  is_cumulative: boolean;
  groups: TimeSeriesGroup[];
}

export interface TimeSeriesFilters {
  testSuiteIds?: string[];
  appConfigIds?: string[];
  groupByAppConfig?: boolean;
}

export type ProviderType = "anchor";

export interface RunTestByIdParams {
  run_id?: string;
  test_id: string;
  provider?: ProviderType;
  test_suite_id?: string;
  test_suite_run_id?: string;
  group_key?: string;
  allow_fix?: boolean;
  run_matrix?: boolean;
  matrix_ids?: string[];
  matrix_target?: string;
}

export interface RunSuiteByIdParams {
  run_id?: string;
  test_suite_id: string;
  provider?: ProviderType;
  trigger: TestSuiteRunTrigger;
}

export type AiRequest<T = unknown> = {
  query: string;
  context?: string[];
  use_app_context?: boolean;
  output_model?: T;
};

export type AiResponse<T = string> = {
  resp: T;
};

export type ScenarioPersistType = "test" | "test_context" | "graph";

export type ScenarioState =
  | "pending"
  | "scheduled"
  | "processing"
  | "processing_error"
  | "paused"
  | "testing"
  | "ready"
  | "persisted"
  | "discarded";

export interface NextTaskBase {
  should_continue: boolean;
  temptative_flow_name: string;
  next_task: string;
}

export interface NextTask extends NextTaskBase {
  started_at?: string;
  finished_at?: string;
}

export function isNextTask(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  task: any,
): task is NextTask {
  return (task as NextTask).next_task !== undefined;
}

export interface ScenarioData {
  app_config_id: string;
  tasks: NextTask[];
  test?: Test;
  test_contexts: TestContext[];
  graph?: Graph;
  intent?: string;
  test_dependency_ids?: string[];
}

export interface Scenario extends AuditBase {
  id: string;

  external_group_id?: string;
  external_id?: string;

  seed_context?: string;
  seed_context_data?: Record<string, unknown>;

  name: string;

  persist_type: ScenarioPersistType;
  persist_id?: string;

  started_processing_at?: string;
  failed_processing_at?: string;
  processing_error?: string;
  ready_at?: string;

  ready_after_seconds?: number;

  recording_path?: string;

  live_view_url?: string;
  test_live_view_url?: string;

  state: ScenarioState;

  data: ScenarioData;
}

export interface RunAutoGenParams {
  app_config_id?: string;
  intent?: string;
  test_dep_ids?: string[];
  fast?: boolean;
  llm_model?: string;
  tester_llm_model?: string;
  decision_llm_model?: string;
}

export interface RunAutoGenResponse {
  app_config_id: string;
  scenario_id: string;
  intent?: string;
  group_id?: string;
  test_dep_ids: string[];
}

export type NotificationSettingsConfigScope = "test" | "test_suite" | "all";

export const NotificationSettingsConfigScopeList: NotificationSettingsConfigScope[] = [
  "test",
  "test_suite",
  "all",
];

export type NotificationSettingsConfigType = "instant" | "daily_summary" | "weekly_summary";

export const NotificationSettingsConfigTypeList: NotificationSettingsConfigType[] = [
  "instant",
  "daily_summary",
  "weekly_summary",
];

export interface NotificationSettingsConfig {
  scope: NotificationSettingsConfigScope;
  on_status: TestRunStatus[];
  type: NotificationSettingsConfigType;
}

export interface NotificationSettings extends AuditBase {
  id: string;
  enabled: boolean;
  onboarding_notifications_enabled: boolean;
  config: NotificationSettingsConfig;
}

export interface OrgSettings {
  weekly_discovery_enabled: boolean;
}

// --- Org flags (JSONB Org.flags) -- see be/db/models.py::OrgFlags ---
// Mirror of `OnboardingFlags`. `completed_at` is an ISO datetime string (or null).
export interface OnboardingFlags {
  completed_at: string | null;
  skipped: boolean;
  completion_reason: "auto" | "user_action" | "skipped" | null;
}

export interface OrgFlags {
  onboarding: OnboardingFlags;
}

// Response body for GET /client/org/settings and PATCH /client/org/settings/flags.
export interface OrgFlagsSettingsResponse {
  flags: OrgFlags;
}

export type CommentEntityType =
  | "general"
  | "dashboard"
  | "generate"
  | "settings"
  | "tests"
  | "test"
  | "test_run"
  | "test_suites"
  | "test_suite"
  | "test_suite_run"
  | "settings"
  | "test_context"
  | "test_step"
  | "graph"
  | "scenario"
  | "scenarios";

export interface Comment extends AuditBase {
  id: string;
  entity_id?: string;
  entity_type: CommentEntityType;
  text: string;
  thread_id?: string;
  resolved: boolean;
  resolved_at?: string; // ISO datetime string
}

export interface CommentReadReceipt {
  comment_id: string;
  read_at: string; // ISO datetime string
}

export interface UnreadCommentsCount {
  unread_count: number;
  total_count: number;
}

export interface GenerateSessionData {
  started_at?: string;

  app_config_id?: string;

  test_dep_ids: string[];

  db_test: Test;
  db_test_ctxs: TestContext[];

  graph_path: GraphEdge[];

  headless: boolean;
  test_headless: boolean;

  variables: TestVariable[];

  // Not needed
  // agent_history: AgentHistoryList;

  history: AgentActionStep[];
  parsed_code?: ParsedCode;

  prompt_context?: string;
  prompt?: string;

  // Not needed
  // prompt_bd?: TestPrompt;

  liveview_id: string;
  liveview_url: string;

  test_liveview_id: string;
  test_liveview_url: string;

  agent_tasks: TestAgentConfigTask[];

  out_dir?: string;

  base_llm_model?: string;
  planner_llm_model?: string;
  planner_interval: number;
  use_vision_for_planner: boolean;
}

export type GenerateSessionState = "idle" | "running" | "testing" | "finished";

export interface GenerateSession extends AuditBase {
  id: string;
  state: GenerateSessionState;
  data: GenerateSessionData;
}

export type AvailableActionType =
  | "click"
  | "type"
  | "store_to_variable"
  | "assert"
  | "select"
  | "check"
  | "uncheck"
  | "hover"
  | "focus"
  | "scroll_to"
  | "mouse_down"
  | "mouse_up";

export interface AvailableAction {
  index?: number;
  types: AvailableActionType[];
  text: string;
  element_tag?: string;
  element_attributes: Record<string, string>;
}

export interface TestImportSchema {
  name: string;
  app_config_id: string;
  variables: TestVariable[];
  test_dependency_ids: string[];
  blocks: Block[];
}

export interface UserFlowStepVariable {
  var_name: string;
  var_type: string;
}

export interface UserFlowStep {
  index: number;
  task: string;
  used_variables?: string[];
  task_variables: UserFlowStepVariable[];
  reasoning: string;
  step_validation?: string;
  url: string;
  aaa_phase?: "arrange" | "act" | "assert";
}

export interface UserFlow {
  slug: string;
  depends_on?: string;
  name: string;
  description: string;
  steps: UserFlowStep[];
  is_positive: boolean;
  success_criteria: string;
  need_user_input: boolean;
  user_input_question?: string;
  reasoning: string;
  confidence: UserFlowConfidence;
  severity: UserFlowSeverity;
  priority: UserFlowPriority;
  expected_behavior?: string;
  is_vulnerability?: boolean;
  error_message?: string;
  recommendations?: string;
  variant?: "discovery" | "edge_case";
}

export interface IssueReport {
  title: string;
  description: string;
  issue_type: IssueType;
  severity: UserFlowSeverity;
  url?: string;
  help?: string;
  recommendations?: string[];
  tags: string[];
}

export interface DiscoveryResult {
  discovered_flows_count: number;
  found_issues: string[];
  issues: IssueReport[];
  human_input_needed: boolean;
  human_input_question?: string;
}

export type DiscoveryRunState =
  | "pending"
  | "user_input_needed"
  | "waiting"
  | "running"
  | "cancelled"
  | "done"
  | "closed";

export interface DiscoveryRun extends AuditBase, DiscoveryResult {
  id: string;
  version?: DiscoveryRunVersion;
  agent_ids?: string[];
  analysis_agent_ids?: string[];
  external_id?: string;
  app_config_id?: string;
  live_view_url?: string;
  recording_path?: string;
  flow_count: number;
  intent?: string;
  persona_id?: string;
  comment_group_key?: string;
  started_at?: string;
  ended_at?: string;
  elapsed_seconds?: number;
  state: DiscoveryRunState;
}

export type DiscoveryRunLogLevel =
  | "info"
  | "warning"
  | "error"
  | "reasoning"
  | "action"
  | "question";

export interface DiscoveryRunLog {
  id: string;
  organization_id?: string;
  run_id: string;
  timestamp: string; // ISO datetime string
  message: string;
  level: DiscoveryRunLogLevel;
}

export type PersistedUserFlowState =
  | "draft"
  | "pending_review"
  | "ready"
  | "persisting"
  | "persisted"
  | "discarded";

export interface PersistedUserFlow extends AuditBase {
  run_id: string;
  id: string;
  slug: string;
  state: PersistedUserFlowState;
  name: string;
  description: string;
  depends_on?: string; // Slug of the dependant flow, where this one starts from
  comment_group_key?: string;
  edge_case_for?: string; // Slug of the flow for which this one is an edge case
  test_id?: string;
  test_dependency_ids: string[];
  data: UserFlow;

  // Tags for filtering and categorization
  tags?: string[];
}

export type DiscoveryRunVersion = "v0" | "v1" | "v3";

export interface RunDiscoveryRunParams {
  discovery_run_id?: string;
  allow_promo?: boolean;

  version?: DiscoveryRunVersion;
  app_config_id?: string;
  flow_count?: number;
  edge_case_target?: string;
  intent?: string;
  persona_id?: string;
  variant?: "discovery" | "edge_case";
  fast?: boolean;
  llm_model?: string;
}

export interface RunDiscoveryRunResponse {
  vesion?: DiscoveryRunVersion;
  app_config_id: string;
  flow_count: number;
  agent_id: string; // the run ID, = to discovery_run_id when provided
  intent?: string;
  persona_id?: string;
}

export interface TestContextVersion extends AuditBase {
  hash: string;
  prev_hash?: string;
  timestamp: string; // ISO datetime string
  message?: string;
  test_id: string;
  data: Block[];
}

export type Me = {
  email_verified?: boolean;
  first_name?: string | null;
  last_name?: string | null;
  id: string;
  image_url?: string | null;
  name?: string | null;
  role?: "admin" | "client" | null;
  organization?: {
    id: string;
  };
  organizations?: {
    [id: string]: string;
  };
};

export type DataAssetType =
  | "raw_text"
  | "raw_json"
  | "text"
  | "json"
  | "csv"
  | "excel"
  | "pdf"
  | "image"
  | "other";

export interface DataAsset extends AuditBase {
  id: string;
  name: string;
  description?: string;
  data_type: DataAssetType;
  file_path?: string;
  file_name?: string;
  raw_data?: string;
}

// Issue related types
export type IssueType =
  | "failed_test"
  | "high_flakiness"
  | "bug"
  | "arrange_error"
  | "assertion_error"
  | "data_integrity_error"
  | "performance_issue"
  | "security_vulnerability"
  | "configuration_error"
  | "third_party_error"
  | "network_error"
  | "observation"
  | "axe_violation"
  | "axe_incomplete"
  | "feature_request"
  | "improvement"
  | "other";

export type IssueStatus = "open" | "closed" | "resolved" | "reopened";

export interface Issue extends AuditBase {
  id: string;
  slug: string;
  external_id?: string;
  last_seen: string;
  seen_count: number;
  title: string;
  description: string;
  test_id?: string;
  test_context_id?: string;
  test_context_block_id?: number;
  discovery_run_id?: string;
  user_flow_id?: string;
  scenario_id?: string;
  hash?: string;
  help?: string;
  help_url?: string;
  url?: string;
  reporter: string;
  recommendations?: string[];
  issue_type: IssueType;
  severity: UserFlowSeverity;
  issue_status: IssueStatus;
  owner?: string;
  resolved_at?: string;
  resolved_by?: string;
  closed_at?: string;
  closed_by?: string;
  tags: string[];
  pre_screenshot_path?: string;
  post_screenshot_path?: string;
  recording_path?: string;
  recording_timestamps?: number[];
  additional_metadata?: Record<string, unknown>;
}

export interface IssueOccurrence extends AuditBase {
  id: string;
  issue_id: string;
  test_run_id?: string;
  test_suite_run_id?: string;
  discovery_run_id?: string;
  description: string;
}

export interface NewIssueData {
  id: string;
  title: string;
  description?: string | null;
  url?: string | null;
  recommendations?: string[] | null;
  help?: string | null;
  help_url?: string | null;
  tags: string[];
  count?: number;
}

export interface NewIssueEvent {
  ts: string; // ISO 8601 datetime string
  type: "new_issue";
  data: NewIssueData;
}

// Browser Storage State types
export interface BrowserCookie {
  name: string;
  value: string;
  domain: string;
  path: string;
  expires: number;
  httpOnly: boolean;
  secure: boolean;
  sameSite: "Lax" | "Strict" | "None";
}

export interface BrowserLocalStorageItem {
  name: string;
  value: string;
}

export interface IndexedDBRecord {
  value: unknown;
}

export interface IndexedDBIndex {
  name: string;
  keyPath: string | string[] | null;
  unique?: boolean;
  multiEntry?: boolean;
}

export interface IndexedDBStore {
  name: string;
  autoIncrement: boolean;
  keyPath: string | string[] | null;
  records: IndexedDBRecord[];
  indexes: IndexedDBIndex[];
}

export interface IndexedDBDatabase {
  name: string;
  version: number;
  stores: IndexedDBStore[];
}

export interface BrowserOrigin {
  origin: string;
  localStorage: BrowserLocalStorageItem[];
  indexedDB?: IndexedDBDatabase[];
}

export interface SessionStorageData {
  [origin: string]: {
    [key: string]: string;
  };
}

export interface BrowserData {
  cookies: BrowserCookie[];
  origins: BrowserOrigin[];
  sessionStorage?: SessionStorageData;
}

// Onboarding
export interface OnboardedData {
  app_configs: number;
  tests: number;
  discovery_runs: number;
}

export interface OnboardedResponse {
  onboarded: boolean;
  data: OnboardedData;
}

// Check Types
export type CheckType = "api" | "script";
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS";
export type RetryStrategy = "none" | "simple" | "exponential";
export type AiValidationMode = "fast" | "max" | "ultra";
export type JsonPathAssertionType = "equals" | "contains" | "exists";
export type CheckScheduleRunTrigger = "manual" | "cron";

export const HTTP_METHODS: HttpMethod[] = [
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
  "HEAD",
  "OPTIONS",
];
export const RETRY_STRATEGIES: RetryStrategy[] = ["none", "simple", "exponential"];
export const AI_VALIDATION_MODES: AiValidationMode[] = ["fast", "max", "ultra"];
export const JSON_PATH_ASSERTION_TYPES: JsonPathAssertionType[] = ["equals", "contains", "exists"];

// Check Config Models
export interface ApiCheckConfig {
  url: string;
  method: HttpMethod;
  headers?: Record<string, string>;
  body?: string;
  follow_redirects?: boolean;
}

export interface JsonPathAssertion {
  path: string;
  type: JsonPathAssertionType;
  expected?: string;
}

// A named value extracted from a check's response, exposed to downstream checks.
export interface CheckOutputCapture {
  name: string;
  json_path: string;
}

export type CheckScheduleFailureStrategy = "stop_on_failure" | "continue_on_failure";

export const CHECK_SCHEDULE_FAILURE_STRATEGIES: CheckScheduleFailureStrategy[] = [
  "stop_on_failure",
  "continue_on_failure",
];

export interface CheckValidation {
  expected_status_code?: number;
  expected_body_contains?: string;
  json_path_assertions?: JsonPathAssertion[];
  ai_validation?: string;
  ai_mode: AiValidationMode;
}

export interface CheckRunRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
  sent_at?: string;
}

export interface CheckRunResponse {
  status_code: number;
  headers: Record<string, string>;
  body: string;
  time_ms: number;
  received_at?: string;
}

// Main Check Model
export interface Check extends AuditBase {
  id: string;
  name: string;
  description?: string;
  app_config_id: string;
  check_type: CheckType;
  config: ApiCheckConfig;
  validation?: CheckValidation;
  variables: TestVariable[];
  retry_strategy: RetryStrategy;
  max_retries: number;
  retry_delay_seconds: number;
  backoff_multiplier: number;
  timeout_ms: number;
  run_status?: TestRunStatus;
  pfs_score?: number;
  test_dependency_id?: string;
  dependency_cache_ttl_seconds?: number;
  alias?: string;
  check_dependency_ids?: string[];
  output_captures?: CheckOutputCapture[];
}

export interface CheckSchema {
  name: string;
  description?: string;
  app_config_id: string;
  check_type?: CheckType;
  config: ApiCheckConfig;
  validation?: CheckValidation;
  variables?: TestVariable[];
  retry_strategy?: RetryStrategy;
  max_retries?: number;
  retry_delay_seconds?: number;
  backoff_multiplier?: number;
  timeout_ms?: number;
  test_dependency_id?: string;
  dependency_cache_ttl_seconds?: number;
  alias?: string;
  check_dependency_ids?: string[];
  output_captures?: CheckOutputCapture[];
}

// Check Run Log Entry
export interface CheckRunLogEntry {
  timestamp: string;
  attempt: number;
  status:
    | "started"
    | "failed"
    | "retrying"
    | "succeeded"
    // Dependency execution statuses
    | "dependency_started"
    | "dependency_completed"
    | "dependency_failed"
    | "dependency_cache_hit";
  message: string;
  delay_seconds?: number;
  // Fields for dependency logging
  test_id?: string;
  test_run_id?: string;
  test_name?: string;
  cache_expires_in_seconds?: number;
}

// Check Run Model
export interface CheckRun extends AuditBase {
  id: string;
  rerun_id?: string;
  check_id: string;
  check_name?: string;
  run_status: TestRunStatus;
  request?: CheckRunRequest;
  response?: CheckRunResponse;
  validation_passed?: boolean;
  validation_reasoning?: string;
  start_time?: string;
  end_time?: string;
  duration_ms?: number;
  error_message?: string;
  retry_count: number;
  logs?: CheckRunLogEntry[];
  check_schedule_id?: string;
  check_schedule_name?: string;
  check_schedule_run_id?: string;
  // Ad-hoc check-chain root (DES-467): set when a standalone check run
  // executed its dependency chain. Every run in the chain — including the
  // target — points at the target's run id.
  root_check_run_id?: string;
  trigger: "manual" | "schedule";
  output_variables?: TestVariable[];
}

// Check Schedule Model
export interface CheckSchedule extends AuditBase {
  id: string;
  name: string;
  description?: string;
  app_config_id: string;
  check_ids: string[];
  cron_expression: string;
  timezone: string;
  enabled: boolean;
  hatchet_cron_id?: string;
  failure_strategy?: CheckScheduleFailureStrategy;
  check_dependency_graph?: Record<string, string[]> | null;
  check_dependency_graph_conditions?: Record<string, string[]> | null;
}

export interface CheckScheduleSchema {
  name: string;
  description?: string;
  app_config_id: string;
  check_ids?: string[];
  cron_expression: string;
  timezone?: string;
  enabled?: boolean;
  failure_strategy?: CheckScheduleFailureStrategy;
  check_dependency_graph_conditions?: Record<string, string[]> | null;
}

// Check Schedule Run Model
export interface CheckScheduleRun extends AuditBase {
  id: string;
  check_schedule_id: string;
  run_status: TestRunStatus;
  start_time?: string;
  end_time?: string;
  duration_seconds?: number;
  total_checks: number;
  passed_checks: number;
  failed_checks: number;
  trigger: CheckScheduleRunTrigger;
}

// ===== Suggested Check Types =====

export type SuggestionStatus = "pending" | "accepted" | "dismissed";

export interface SuggestedCheck extends AuditBase {
  id: string;
  test_id: string;
  test_run_id: string;
  app_config_id: string;
  url: string;
  method: HttpMethod;
  headers: Record<string, string>;
  body?: string;
  suggested_auth_headers: string[];
  source_test_ids: string[];
  confidence_score: number;
  suggestion_status: SuggestionStatus;
  accepted_check_id?: string;
}

export interface AcceptSuggestedCheckRequest {
  name: string;
  description?: string;
  selected_headers: string[];
  test_dependency_id?: string;
  dependency_cache_ttl_seconds?: number;
  validation?: CheckValidation;
}

export interface AnalyzeHarRequest {
  include_all_domains?: boolean;
}

export interface AnalyzeHarResponse {
  workflow_run_id: string;
  status: string;
}

export interface SuggestedChecksResponse {
  suggestions: SuggestedCheck[];
  total: number;
}

export interface BatchSuggestedCheckRequest {
  suggestion_ids: string[];
}

export interface BatchAcceptResponse {
  accepted: number;
  check_ids: string[];
  errors: string[];
}

export interface BatchDismissResponse {
  dismissed: number;
  errors: string[];
}

// ===== Status Page Types =====

export type DailyStatusType = "operational" | "degraded" | "outage" | "no_data";
export type OverallStatusType = "operational" | "degraded" | "outage" | "unknown";

export interface DailyStatus {
  date: string;
  status: DailyStatusType;
  total_runs: number;
  passed_runs: number;
  failed_runs: number;
}

export interface CheckStatusSummary {
  check_id: string;
  check_name: string;
  current_status?: TestRunStatus;
  uptime_percentage: number;
  daily_statuses: DailyStatus[];
}

export interface ScheduleStatusSummary {
  schedule_id: string;
  schedule_name: string;
  description?: string;
  enabled: boolean;
  current_status: OverallStatusType;
  uptime_percentage: number;
  total_checks: number;
  daily_statuses: DailyStatus[];
  checks?: CheckStatusSummary[]; // Lazy loaded on accordion expand
}

export interface StatusPageResponse {
  overall_status: OverallStatusType;
  overall_uptime_percentage: number;
  time_range_days: number;
  schedules: ScheduleStatusSummary[];
  last_updated: string;
}

// ===== Support Chat Types =====

export type SupportConversationStatus = "open" | "closed";
export type SupportMessageSender = "customer" | "support";

export interface SupportConversation {
  id: string;
  user_id: string;
  user_name: string;
  user_image_url: string | null;
  subject: string | null;
  conversation_status: SupportConversationStatus;
  slack_thread_ts: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface SupportMessage {
  id: string;
  conversation_id: string;
  sender_type: SupportMessageSender;
  sender_id: string | null;
  sender_name: string;
  sender_avatar: string | null;
  content: string;
  image_url: string | null;
  slack_ts: string | null;
  created_at: string;
}

// ===== User Stories KB Types =====

export type UserStoryStatus = "covered" | "partial" | "uncovered" | "broken";

export interface StoryInsight {
  ts: string;
  by: string;
  note: string;
}

export interface UserStory {
  id: string;
  title: string;
  description: string;
  priority: UserFlowPriority;
  status: UserStoryStatus;
  covered_by: string[];
  gaps: string[];
  insights: StoryInsight[];
  created_at: string;
  created_by?: string;
  updated_at?: string;
}

export interface SuggestedStory {
  id: string;
  title: string;
  description: string;
  priority: UserFlowPriority;
  source: string;
  discovered_at: string;
}

export interface UserStoriesRegistry {
  version: number;
  app_config_id: string;
  created_at: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  stories: UserStory[];
  suggested: SuggestedStory[];
}
